import i18n from "@/i18n";
/**
 * VuosilomaCard — VUOSILOMA-V1. Team-profile annual-leave section
 * (Vuosilomalaki / TES §27 ledger foundation). Separate from VV.
 * Renders backend state only; no entitlement math in React.
 */
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import VuosilomaBasisDialog from "./VuosilomaBasisDialog";
import VuosilomaCorrectionDialog from "./VuosilomaCorrectionDialog";
import VuosilomaEvidenceDialog from "./VuosilomaEvidenceDialog";
import VuosilomaHistory from "./VuosilomaHistory";
import VuosilomaOpeningDialog from "./VuosilomaOpeningDialog";
import { YearCard } from "./VuosilomaYears";
import { browserLocalYMD, formatYMDDate } from "./workforceHelpers";
import {
    creditYearLabel, vlBasisLabel, vlDays, vlHoldText, vlReasonText, VL_BOOKING_STATE,
} from "./vuosilomaHelpers";

function Pending({ rows }) {
    const live = (rows || []).filter((p) => p.pending_days > 0);
    if (!live.length) return null;
    return (
        <div data-testid="vl-pending">
            <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-neutral-500">
                {i18n.t("wf.vuosilomaCard.bookedNotFinalized", "Booked, not finalized")}
            </h4>
            <ul className="space-y-1">
                {live.map((p) => (
                    <li key={p.absence_id} data-testid={`vl-pending-${p.absence_id}`}
                        className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900">
                        {formatYMDDate(p.start_date)} – {formatYMDDate(p.end_date)} · {vlDays(p.pending_days)}
                        {" · "}{creditYearLabel(p.funding_year)}
                        <div className="text-xs" data-testid={`vl-pending-state-${p.absence_id}`}>
                            {VL_BOOKING_STATE[p.booking_state]}
                        </div>
                        {(p.held_dates || []).map((h) => (
                            <div key={h.entry_id} className="text-xs text-amber-800"
                                data-testid={`vl-pending-hold-${h.date}`}>
                                Finalized day held back · {vlHoldText(h)}
                            </div>
                        ))}
                    </li>
                ))}
            </ul>
        </div>
    );
}

// canManageFacts — opening, earning rule, evidence, corrections: Manager/Corporate only (Super Admin denied).
export default function VuosilomaCard({ employeeId, restaurantId, canManageFacts, reloadKey }) {
    const [state, setState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [openingOpen, setOpeningOpen] = useState(false);
    const [basisOpen, setBasisOpen] = useState(false);
    const [evidenceOpen, setEvidenceOpen] = useState(false);
    const [correcting, setCorrecting] = useState(null);

    const load = useCallback(async () => {
        if (!employeeId) return;
        setLoading(true);
        setError(false);
        try {
            const { data } = await api.get(`/workforce/employees/${employeeId}/vuosiloma`,
                { params: { as_of: browserLocalYMD() } });
            setState(Array.isArray(data?.credit_years) ? data : null);
            if (!Array.isArray(data?.credit_years)) setError(true);
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [employeeId]);

    useEffect(() => { load(); }, [load, reloadKey]);

    let body;
    if (loading && !state) {
        body = <Skeleton className="h-24 w-full" data-testid="vl-card-loading" />;
    } else if (error || !state) {
        body = (
            <div data-testid="vl-card-error" className="text-sm text-neutral-600">
                Annual leave could not be loaded.{" "}
                <Button variant="link" size="sm" onClick={load} data-testid="vl-card-retry">{i18n.t("wf.vuosilomaCard.retry", "Retry")}</Button>
            </div>
        );
    } else {
        body = (
            <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
                    <span>{i18n.t("wf.vuosilomaCard.earningRule", "Earning rule:")} <b data-testid="vl-basis">{vlBasisLabel(state.earning_basis)}</b></span>
                    <span>{i18n.t("wf.vuosilomaCard.opening", "Opening:")} <b data-testid="vl-opening-status">
                        {state.opening_effective_date ? formatYMDDate(state.opening_effective_date) : i18n.t("wf.vuosilomaCard.notRecorded", "Not recorded")}
                    </b></span>
                    <span className="text-neutral-500">Employment start {formatYMDDate(state.employment_start_date)}</span>
                </div>
                {(state.status_reasons || []).length > 0 && (
                    <ul data-testid="vl-status-reasons"
                        className="space-y-1 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                        {(state.status_reasons || []).map((r) => (
                            <li key={r} data-testid={`vl-status-reason-${r}`}>{vlReasonText(r)}</li>
                        ))}
                    </ul>
                )}
                <div className="space-y-2" data-testid="vl-years">
                    {state.credit_years.length === 0 && (
                        <p className="text-sm text-neutral-500" data-testid="vl-years-empty">{i18n.t("wf.vuosilomaCard.noHolidayYearsToShowYet", "No holiday years to show yet.")}</p>
                    )}
                    {[...state.credit_years].reverse().map((y) => <YearCard key={y.credit_year_start} y={y} />)}
                </div>
                <Pending rows={state.pending} />
                <details data-testid="vl-history-section">
                    <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-neutral-500">
                        Entry history ({(state.history || []).length})
                    </summary>
                    <VuosilomaHistory history={state.history} canManage={canManageFacts} onCorrect={setCorrecting} />
                </details>
                <p className="text-xs text-neutral-400" data-testid="vl-scope-note">
                    {i18n.t("wf.vuosilomaCard.annualLeaveLedgerFoundationSomeStatutory", "Annual-leave ledger foundation. Some statutory and TES cases are not calculated yet and are shown as unresolved. Holiday pay is not calculated.")}
                </p>
            </div>
        );
    }

    return (
        <section data-testid="vuosiloma-card">
            <div className="mb-3 flex items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-neutral-700">{i18n.t("wf.vuosilomaCard.vuosilomaAnnualLeave", "Vuosiloma / Annual leave")}</h3>
                {canManageFacts && state && (
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" data-testid="vl-set-basis-btn"
                            onClick={() => setBasisOpen(true)}>{i18n.t("wf.vuosilomaCard.earningRule2", "Earning rule")}</Button>
                        <Button variant="outline" size="sm" data-testid="vl-opening-btn"
                            onClick={() => setOpeningOpen(true)}>
                            {state.opening_effective_date ? i18n.t("wf.vuosilomaCard.addOpeningFact", "Add opening fact") : i18n.t("wf.vuosilomaCard.recordOpening", "Record opening")}
                        </Button>
                        <Button variant="outline" size="sm" data-testid="vl-evidence-btn"
                            onClick={() => setEvidenceOpen(true)}>{i18n.t("wf.vuosilomaCard.addEvidence", "Add evidence")}</Button>
                    </div>
                )}
            </div>
            <div className="rounded-md border border-neutral-200 bg-neutral-50/40 p-4">{body}</div>
            <VuosilomaOpeningDialog open={openingOpen} onOpenChange={setOpeningOpen}
                employeeId={employeeId} boundary={state?.opening_effective_date} onSaved={load} />
            <VuosilomaBasisDialog open={basisOpen} onOpenChange={setBasisOpen} employeeId={employeeId}
                restaurantId={restaurantId} currentBasis={state?.earning_basis} onSaved={load} />
            <VuosilomaEvidenceDialog open={evidenceOpen} onOpenChange={setEvidenceOpen}
                employeeId={employeeId} restaurantId={restaurantId} onSaved={load} />
            <VuosilomaCorrectionDialog entry={correcting} onOpenChange={(v) => !v && setCorrecting(null)}
                employeeId={employeeId} onSaved={load} />
        </section>
    );
}
